import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/dashboard/Sidebar';
import { Send, Bot, User } from 'lucide-react';
import axios from 'axios';

const DashboardMentor = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const STUDENT_ID = 'student-demo-1';
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Add welcome message
    setMessages([{
      id: 'welcome',
      text: "Hello! I'm your AI Career Mentor. I'm here to help you with your courses, answer questions about IT careers, and provide guidance. How can I assist you today?",
      isStudent: false,
      timestamp: new Date().toISOString()
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      text: inputMessage,
      isStudent: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/dashboard/mentor/chat`, {
        student_id: STUDENT_ID,
        message: inputMessage,
        session_id: sessionId
      });

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const aiMessage = {
        id: Date.now().toString() + '-ai',
        text: response.data.response,
        isStudent: false,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now().toString() + '-error',
        text: "Sorry, I'm having trouble connecting right now. Please try again.",
        isStudent: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900">AI Career Mentor</h1>
          <p className="text-gray-600 mt-1">Get instant answers and guidance 24/7</p>
        </div>

        {/* Chat Container */}
        <div className="flex-1 overflow-hidden flex flex-col p-6 max-w-4xl mx-auto w-full">
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto mb-4 space-y-4 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.isStudent ? 'flex-row-reverse' : 'flex-row'}`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  message.isStudent 
                    ? 'bg-gradient-to-br from-[#0D47A1] to-[#00BCD4]' 
                    : 'bg-purple-100'
                }`}>
                  {message.isStudent ? (
                    <User className="w-5 h-5 text-white" />
                  ) : (
                    <Bot className="w-5 h-5 text-purple-600" />
                  )}
                </div>
                
                <div className={`flex-1 max-w-[70%] ${
                  message.isStudent ? 'flex flex-col items-end' : ''
                }`}>
                  <div className={`rounded-2xl px-4 py-3 ${
                    message.isStudent 
                      ? 'bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] text-white' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    <p className="whitespace-pre-wrap">{message.text}</p>
                  </div>
                  <span className="text-xs text-gray-500 mt-1 px-2">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-purple-600" />
                </div>
                <div className="bg-gray-100 rounded-2xl px-4 py-3">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={handleSendMessage} className="flex gap-3 bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask your AI Mentor anything..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || loading}
              className="px-6 py-3 bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </form>

          {/* Quick Suggestions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setInputMessage('What courses should I focus on for AI career?')}
              className="px-4 py-2 bg-blue-50 text-[#0D47A1] rounded-lg hover:bg-blue-100 transition-colors text-sm"
            >
              🎯 Career advice
            </button>
            <button
              onClick={() => setInputMessage('How can I improve my course progress?')}
              className="px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors text-sm"
            >
              📊 Study tips
            </button>
            <button
              onClick={() => setInputMessage('What are the job prospects in Australia for cloud engineers?')}
              className="px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm"
            >
              💼 Job market
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardMentor;